import { supabase } from '../lib/supabase';

export const postsService = {
  // Get all published posts (public access)
  async getPublishedPosts() {
    const { data, error } = await supabase
      ?.from('posts')
      ?.select(`
        *,
        user_profiles:author_id (
          full_name,
          avatar_url
        )
      `)
      ?.eq('status', 'published')
      ?.not('published_at', 'is', null)
      ?.order('published_at', { ascending: false });
    
    // Temporary debug logging
    console.log('🔍 DEBUG: Published posts query result:', {
      postsCount: data?.length || 0,
      firstPostSlug: data?.[0]?.slug || 'none',
      firstPostTitle: data?.[0]?.title || 'none',
      firstPostPublishedAt: data?.[0]?.published_at || 'none',
      error: error?.message || 'none'
    });
    
    if (error) throw error;
    return data?.map(post => this.convertToFrontend(post)) || [];
  },

  // Get single published post by slug (public access)
  async getPublishedPostBySlug(slug) {
    const { data, error } = await supabase
      ?.from('posts')
      ?.select(`
        *,
        user_profiles:author_id (
          full_name,
          avatar_url
        )
      `)
      ?.eq('slug', slug)
      ?.eq('status', 'published')
      ?.not('published_at', 'is', null)
      ?.single();
    
    if (error) throw error;
    return this.convertToFrontend(data);
  },

  // Get all posts (admin/editor access)
  async getAllPosts() {
    const { data, error } = await supabase?.from('posts')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(post => this.convertToFrontend(post)) || [];
  },

  // Get single post by ID (admin/editor access)
  async getPostById(id) {
    const { data, error } = await supabase?.from('posts')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;
    return this.convertToFrontend(data);
  },

  // Create new post
  async createPost(postData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('posts')?.insert({
        title: postData?.title,
        slug: postData?.slug,
        excerpt: postData?.excerpt,
        content: postData?.content,
        hero_image_url: postData?.heroImageUrl,
        hero_image_alt: postData?.heroImageAlt,
        status: postData?.status || 'draft',
        author_id: user?.id
      })?.select()?.single();
    
    if (error) throw error;
    return this.convertToFrontend(data);
  },

  // Update post
  async updatePost(id, postData) {
    const updateData = {
      title: postData?.title,
      slug: postData?.slug,
      excerpt: postData?.excerpt,
      content: postData?.content,
      hero_image_url: postData?.heroImageUrl || postData?.heroImage,
      hero_image_alt: postData?.heroImageAlt,
      status: postData?.status
    };

    const { data, error } = await supabase?.from('posts')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return this.convertToFrontend(data);
  },

  // Delete post
  async deletePost(id) {
    const { error } = await supabase?.from('posts')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  // Delete multiple posts
  async deletePosts(ids) {
    const { error } = await supabase?.from('posts')?.delete()?.in('id', ids);
    
    if (error) throw error;
  },

  // Convert database snake_case to frontend camelCase
  convertToFrontend(post) {
    if (!post) return null;
    return {
      id: post?.id,
      title: post?.title,
      slug: post?.slug,
      excerpt: post?.excerpt,
      content: post?.content,
      heroImage: post?.hero_image_url,
      heroImageAlt: post?.hero_image_alt,
      status: post?.status,
      authorId: post?.author_id,
      author: post?.user_profiles?.full_name || 'Unknown Author',
      authorAvatar: post?.user_profiles?.avatar_url || '',
      authorAvatarAlt: `${post?.user_profiles?.full_name || 'Author'} avatar`,
      createdAt: post?.created_at,
      updatedAt: post?.updated_at,
      publishedAt: post?.published_at
    };
  }
};